// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * An SN with the code printed on its label — a robot's as a QR code, a module's as a
 * Code 128 barcode (component design §12). Shown once the SN exists, which is once its
 * archive is filed: on the archive page, and in a filing that has just filed a module on
 * the spot. A scan of either code types the SN into the search.
 *
 * Outside production the code is stamped 「测试」 and the label does not print
 * (`useLabelPrinting`): an SN generated there may repeat a real one.
 */

import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLabelPrinting } from "@/hooks/useLabelPrinting";
import { code128Path } from "@/labels/code128";
import { printLabels, type Symbology } from "@/labels/printLabels";
import { qrPath } from "@/labels/qr";
import { CopyButton } from "./CopyButton";

interface Props {
  sn: string;
  /** `qr` for a robot, `code128` for a module. */
  symbology: Symbology;
  /** Shown above the SN: "SN", "模组 SN". */
  label: string;
  /** A line under the SN. */
  note?: string;
  /** Offers 「打印标签」, with these lines under the SN on the label (§12). */
  printLines?: string[];
}

export function SnCard({ sn, symbology, label, note, printLines }: Props) {
  const { t } = useTranslation();
  const { printable, isTest } = useLabelPrinting();
  const classes = ["robot-sn", symbology === "code128" && "is-barcode", isTest && "is-test"];
  return (
    <figure className={classes.filter(Boolean).join(" ")}>
      <span className="robot-sn-symbol">
        <SnSymbol sn={sn} symbology={symbology} />
        {isTest && (
          <span className="robot-sn-test-mark" aria-hidden="true">
            {t("robotSn.testMark")}
          </span>
        )}
      </span>
      <figcaption>
        <span className="robot-sn-label">{label}</span>
        <span className="robot-sn-value">
          <span className="mono">{sn}</span>
          <CopyButton value={sn} label={t("robotSn.copy")} />
        </span>
        {note && <span className="hint">{note}</span>}
        {isTest && <span className="hint hint-error">{t("robotSn.testOnly")}</span>}
        {printLines && (
          <PrintLabelsButton
            className="robot-sn-print"
            printable={printable}
            onPrint={() => printLabels([{ sn, symbology, lines: printLines }], sn)}
          />
        )}
      </figcaption>
    </figure>
  );
}

/** 「打印标签」, disabled outside production with the reason as its tooltip. The tooltip
 *  sits on a wrapper, because a disabled button gets no hover; the wrapper is there either
 *  way, so the button is the same element before and after the server answers. */
export function PrintLabelsButton({
  printable,
  onPrint,
  className,
}: {
  printable: boolean;
  onPrint: () => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const reason = printable ? undefined : t("robotSn.printProdOnly");
  return (
    <span
      className={printable ? "print-labels" : "print-labels tooltip-trigger"}
      data-tooltip={reason}
    >
      <button
        type="button"
        className={className}
        disabled={!printable}
        title={reason}
        onClick={onPrint}
      >
        {t("robotSn.print")}
      </button>
    </span>
  );
}

function SnSymbol({ sn, symbology }: { sn: string; symbology: Symbology }) {
  const { t } = useTranslation();
  const drawn = useMemo(() => {
    if (symbology === "qr") {
      const { path, size } = qrPath(sn);
      return { path, width: size, height: size };
    }
    const { path, width } = code128Path(sn);
    return { path, width, height: 1 };
  }, [sn, symbology]);
  return (
    <svg
      className={symbology === "qr" ? "robot-sn-qr" : "robot-sn-barcode"}
      viewBox={`0 0 ${drawn.width} ${drawn.height}`}
      // A barcode is one unit tall, stretched to the height CSS gives it.
      preserveAspectRatio={symbology === "qr" ? undefined : "none"}
      role="img"
      aria-label={t(symbology === "qr" ? "robotSn.qrLabel" : "robotSn.barcodeLabel", { sn })}
      shapeRendering="crispEdges"
    >
      <rect width={drawn.width} height={drawn.height} fill="#fff" />
      <path d={drawn.path} fill="#000" />
    </svg>
  );
}
